/**
 * **배포 판 어긋남** — 수집 응답이 실어 주는 서버 판 버전을 이 번들의 판과 견준다.
 *
 * ```
 *   middleware        /api/** 응답에 `x-app-version` 을 싣는다
 *     ▼
 *   queue 의 fetch    응답 헤더를 읽어 noteServerAppVersion 에 넘긴다
 *     ▼
 *   이 파일           둘을 견주고, 어긋나면 구독자에게 한 번 알린다
 *     ▼
 *   cmui 다이얼로그   「새 버전이 배포되었습니다」
 * ```
 *
 * ## 견주는 규칙
 * - **꼬리 없는 semver(`x.y.z`) 둘일 때만** 견준다. `-preview.<sha>`·`-dev` 는 같은 숫자라도
 *   다른 빌드라 「새 버전」이라 말할 근거가 없다 — 비교하지 않는다.
 * - 서버가 더 클 때만 어긋남이다. 같거나 작으면 아니다(낮은 판이 뜬 배포에 새로고침을 권하지 않는다).
 * - 어긋남은 **이 페이지 수명에 한 번만** 알린다. 사용자가 닫았으면 그걸로 끝이다.
 *
 * 이 파일은 아무것도 import 하지 않는다 — middleware(Edge)가 헤더 이름을 여기서 가져간다.
 */

/** 서버가 배포 판을 싣는 응답 헤더 */
export const APP_VERSION_HEADER = 'x-app-version';

/** 서버 판이 이 번들보다 새 판인가 — 둘 중 하나라도 꼬리 달린 버전이면 판정하지 않는다(false) */
export function isServerVersionNewer(
  clientVersion: string | null | undefined,
  serverVersion: string | null | undefined
): boolean {
  const mine = parseReleaseVersion(clientVersion);
  const theirs = parseReleaseVersion(serverVersion);
  if (!mine || !theirs) return false;
  for (let i = 0; i < 3; i += 1) {
    if (theirs[i] !== mine[i]) return theirs[i] > mine[i];
  }
  return false;
}

/** `x.y.z` 만 숫자 셋으로 — 꼬리(`-preview.<sha>`·`-dev`)가 붙었거나 모양이 다르면 null */
export function parseReleaseVersion(value: string | null | undefined): [number, number, number] | null {
  const matched = /^(\d+)\.(\d+)\.(\d+)$/.exec(String(value ?? '').trim());
  if (!matched) return null;
  return [Number(matched[1]), Number(matched[2]), Number(matched[3])];
}

/** 응답 헤더에서 읽은 서버 판을 알린다 — 어긋남이면 구독자를 한 번 깨운다 */
export function noteServerAppVersion(
  serverVersion: string | null | undefined,
  clientVersion: string | null | undefined
): void {
  if (detected) return;
  if (!isServerVersionNewer(clientVersion, serverVersion)) return;
  detected = true;
  listeners.forEach((listener) => listener());
}

/** 어긋남을 한 번 받는다. 이미 어긋난 뒤에 붙어도(늦게 마운트된 화면) 곧바로 받는다 */
export function subscribeVersionSkew(listener: () => void): () => void {
  if (detected) {
    listener();
    return () => {};
  }
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

/** 테스트용 — 상태를 처음으로 */
export function resetVersionSkewForTests(): void {
  detected = false;
  listeners.clear();
}

// ---- private ------------------------------------------------------------

const listeners = new Set<() => void>();
let detected = false;
